import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { userPreferencesService } from '../../../services/supabaseService';

const CompanyBlockingSettings = ({ userRole, onSave }) => {
  const { user } = useAuth();
  const [blockedCompanies, setBlockedCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;

      try {
        const prefs = await userPreferencesService?.get(user?.id);
        if (prefs?.excludedCompanies) {
          setBlockedCompanies(prefs?.excludedCompanies);
        }
      } catch (err) {
        console.error('Error loading blocked companies:', err);
      }
    };

    loadSettings();
  }, [user]);

  const handleAddCompany = async () => {
    if (!newCompany?.trim() || !user) return;

    const updated = [...blockedCompanies, newCompany?.trim()];
    setBlockedCompanies(updated);
    setNewCompany('');

    try {
      await userPreferencesService?.upsert(user?.id, {
        excludedCompanies: updated
      });
      onSave?.('Company blocked successfully');
    } catch (err) {
      console.error('Error blocking company:', err);
    }
  };

  const handleRemoveCompany = async (company) => {
    if (!user) return;

    const updated = blockedCompanies?.filter((c) => c !== company);
    setBlockedCompanies(updated);

    try {
      await userPreferencesService?.upsert(user?.id, {
        excludedCompanies: updated
      });
      onSave?.('Company unblocked successfully');
    } catch (err) {
      console.error('Error unblocking company:', err);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Company Blocking</h2>
        <p className="text-sm text-[#6B7280]">
          Block companies to prevent their job postings from appearing in your opportunity feed. This is useful to avoid seeing positions at your current or former employers.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} color="#3b82f6" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">
              Why block companies?
            </p>
            <p className="text-sm text-blue-700">
              Prevent awkward situations like applying to your own former position anonymously. Blocked companies' job postings will not appear in your feed.
            </p>
          </div>
        </div>
      </div>

      {/* Blocked Companies List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-3">
            <Icon name="Ban" size={20} color="#5B2DFF" />
            <h3 className="text-lg font-semibold text-[#111827]">
              Blocked Companies ({blockedCompanies?.length})
            </h3>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="outline"
            iconName="Plus"
            size="sm"
          >
            Add Company
          </Button>
        </div>

        {/* Add Company Form */}
        {showAddForm && (
          <div className="bg-[#F6F7FB] rounded-xl p-4 space-y-3">
            <Input
              label="Company Name"
              placeholder="e.g., EBC Construction, Tech Corp Inc."
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e?.target?.value)}
              onKeyPress={(e) => {
                if (e?.key === 'Enter') {
                  handleAddCompany();
                }
              }}
            />
            <div className="flex gap-2">
              <Button
                onClick={handleAddCompany}
                variant="primary"
                size="sm"
                disabled={!newCompanyName?.trim()}
              >
                Add to Blocked List
              </Button>
              <Button
                onClick={() => {
                  setShowAddForm(false);
                  setNewCompanyName('');
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Blocked Companies List */}
        {blockedCompanies?.length > 0 ? (
          <div className="space-y-2">
            {blockedCompanies?.map((company, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <Icon name="Building2" size={20} color="#ef4444" />
                  </div>
                  <div>
                    <p className="font-medium text-[#111827]">{company}</p>
                    <p className="text-xs text-[#6B7280]">Job postings from this company are hidden</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleRemoveCompany(company)}
                  variant="outline"
                  iconName="X"
                  size="sm"
                  className="text-red-600 hover:bg-red-50 border-red-200"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#F6F7FB] rounded-xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Icon name="Building2" size={32} color="#9CA3AF" />
            </div>
            <p className="text-[#6B7280] font-medium mb-1">No blocked companies</p>
            <p className="text-sm text-[#9CA3AF]">
              Add companies to prevent their job postings from appearing in your feed
            </p>
          </div>
        )}
      </div>

      {/* Usage Tips */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Icon name="Lightbulb" size={20} color="#9333ea" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-900 mb-2">
              Tips for effective blocking:
            </p>
            <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
              <li>Add your current employer to avoid seeing internal postings</li>
              <li>Block former employers if you don't want to return</li>
              <li>Use the exact company name as it appears on job postings</li>
              <li>You can remove companies from the list anytime</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyBlockingSettings;